import * as React from 'react';
import { useXyqarAuth } from '@xyqar/shared-auth';
import { apiGet } from '../lib/api';
import { PageTitle, Card, EmptyState, Section, Kpi, Pill, Btn, fmt } from '../components/ui-kit';

interface Payment { id: string; date: string; amount_eur: number; status: string; invoice_url?: string; }

export default function Payments() {
  const { getToken } = useXyqarAuth();
  const [items, setItems] = React.useState<Payment[] | null>(null);

  React.useEffect(() => {
    (async () => {
      try { const tok = await getToken().catch(() => ''); const j = await apiGet<{ payments: Payment[] }>('/api/v1/expert/payments', tok); setItems(j.payments ?? []); }
      catch { setItems([]); }
    })();
  }, [getToken]);

  const paidItems = (items ?? []).filter((payment) => (payment.status ?? '').toLowerCase() === 'paid');
  const pendingItems = (items ?? []).filter((payment) => (payment.status ?? '').toLowerCase() !== 'paid');
  const totalAmount = (items ?? []).reduce((sum, payment) => sum + (payment.amount_eur ?? 0), 0);
  const paidAmount = paidItems.reduce((sum, payment) => sum + (payment.amount_eur ?? 0), 0);
  const invoiceCoverage = (items ?? []).filter((payment) => payment.invoice_url).length;

  return (
    <div>
      <PageTitle kicker="Earnings" title="Payments" />

      <Section title="At a glance" hint="This page reflects expert-side payout visibility only.">
        <div className="grid grid-cols-2 sm:grid-cols-4 gap-4">
          <Kpi label="Total amount" value={`${fmt(Math.round(totalAmount))} €`} />
          <Kpi accent="green" label="Paid" value={`${fmt(Math.round(paidAmount))} €`} />
          <Kpi accent="amber" label="Pending items" value={fmt(pendingItems.length)} />
          <Kpi label="Invoices attached" value={fmt(invoiceCoverage)} hint={`${fmt(items?.length)} rows total`} />
        </div>
      </Section>

      <Section title="Payout status">
        <div className="grid gap-4 lg:grid-cols-[1.35fr,0.65fr]">
          <Card>
            <p className="text-sm text-white">Payments move from mission validation to invoice publication to settlement.</p>
            <p className="text-xs text-white/60 mt-2 leading-relaxed">
              If a row is still pending, check mission CRA approval and invoice issuance before escalating finance. Invoice PDFs appear only when finance has published the corresponding document.
            </p>
          </Card>
          <Card>
            <p className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono">Breakdown</p>
            <div className="mt-3 space-y-2 text-xs text-white/70">
              <p>{paidItems.length} paid item(s).</p>
              <p>{pendingItems.length} awaiting settlement or document publication.</p>
              <p>{Math.max((items ?? []).length - invoiceCoverage, 0)} item(s) without PDF yet.</p>
            </div>
          </Card>
        </div>
      </Section>

      <Section title="Payment ledger">
        {items === null && <Card>Loading…</Card>}
        {items && items.length === 0 && <EmptyState title="No payments yet" hint="Validated invoices will appear here once finance emits the settlement line." />}
        {items && items.length > 0 && (
          <Card>
            <table className="w-full text-sm">
              <thead className="text-[10px] uppercase tracking-[0.2em] text-white/40 font-mono"><tr><th className="text-left py-3">Date</th><th className="text-left py-3">Status</th><th className="text-right py-3">Amount</th><th className="text-right py-3">Invoice</th></tr></thead>
              <tbody>
                {items.map((p) => (
                  <tr key={p.id} className="border-t border-white/5">
                    <td className="py-3 font-mono">{p.date}</td>
                    <td className="py-3 text-white/60"><Pill tone={(p.status ?? '').toLowerCase() === 'paid' ? 'live' : 'warn'}>{p.status}</Pill></td>
                    <td className="py-3 text-right">{p.amount_eur.toLocaleString('fr-FR')} €</td>
                    <td className="py-3 text-right">
                      {p.invoice_url ? <Btn as="a" href={p.invoice_url}>PDF</Btn> : <span className="text-white/30 text-xs">pending publish</span>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </Card>
        )}
      </Section>
    </div>
  );
}
